// src/scenes/groupOfficials/index.jsx
import React, { useMemo, useState } from "react";
import { Box, useTheme,
  useMediaQuery, Snackbar, Alert, Button, Tabs, Tab, Chip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery, useMutation, gql } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import Header from "components/Header";

import GroupOfficialsForm from "./form";
import ApprovalSettingsForm from "./approvalForm";

import { groupClient, userClient } from "state/apolloClient";

const GET_GROUP_OFFICIALS = gql`
  query {
    groupOfficials {
      id
      userId
      username
      positionId
      position
      groupId
      assignedDate
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const GET_GROUP_POSITIONS = gql`
  query {
    groupPositions {
      id
      positionName
      description
      groupId
      groupName
    }
  }
`;

const GET_USERS = gql`
  query {
    users(user: {}) {
      id
      firstName
      lastName
      email
      phoneNumber
      role
      group
      isActive
      roleId
    }
  }
`;

const ASSIGN_POSITIONS_BULK = gql`
  mutation AssignPositionsBulk($assignments: [GroupOfficialsInput!]!) {
    assignPositionsBulk(assignments: $assignments) {
      insertedCount
      updatedCount
      skippedCount
    }
  }
`;

const GET_RECORD_APPROVAL_SETTINGS = gql`
  query {
    recordApprovalSettings {
      approver
      approverId
      createdBy
      createdDate
      group
      groupId
      id
      isActive
      locked
      tableName
      updatedBy
      updatedDate
    }
  }
`;

const CREATE_RECORD_APPROVAL_SETTING = gql`
  mutation Create($newSetting: RecordApprovalSettingsInput!) {
    createRecordApprovalSetting(newSetting: $newSetting) {
      id
      tableName
      approverId
      isActive
      locked
      createdDate
      updatedDate
    }
  }
`;

const UPDATE_RECORD_APPROVAL_SETTING = gql`
  mutation Update($updatedSetting: RecordApprovalSettingsInput!) {
    updateRecordApprovalSetting(updatedSetting: $updatedSetting) {
      id
      tableName
      approverId
      isActive
      locked
      createdDate
      updatedDate
    }
  }
`;

const APPROVAL_TABLES = [
  { key: "MonthlyContributions", label: "Monthly Contributions" },
  { key: "ContributionScheduleDeletionRequest", label: "Contribution Schedule Deletion" },
  { key: "AccountTransactions", label: "Account Transactions" },
];
const fmtDate = (value) => (value ? new Date(value).toLocaleDateString() : "-");

const gridSx = (theme) => ({
  "& .MuiDataGrid-root": { border: "none" },
  "& .MuiDataGrid-cell": { borderBottom: "none" },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.secondary[100],
    borderBottom: "none",
  },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: theme.palette.primary.light },
  "& .MuiDataGrid-footerContainer": {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.secondary[100],
    borderTop: "none",
  },
});

// =============================
// ✅ Apollo/GraphQL error helper
// - Prioritizes HotChocolate "extensions.message"
// =============================
const getApolloErrorMessage = (err) => {
  // 1) Standard GraphQL errors
  const gqlErr = err?.graphQLErrors?.[0];
  if (gqlErr) {
    const extMsg = gqlErr?.extensions?.message;
    if (typeof extMsg === "string" && extMsg.trim()) return extMsg;

    // Sometimes BE puts useful info here
    const extAlt = gqlErr?.extensions?.errorMessage;
    if (typeof extAlt === "string" && extAlt.trim()) return extAlt;

    if (typeof gqlErr.message === "string" && gqlErr.message.trim()) return gqlErr.message;
  }

  // 2) Network error that still contains a GraphQL-like payload
  const netErrMsg =
    err?.networkError?.result?.errors?.[0]?.extensions?.message ||
    err?.networkError?.result?.errors?.[0]?.message;

  if (typeof netErrMsg === "string" && netErrMsg.trim()) return netErrMsg;

  // 3) Fallback
  if (typeof err?.message === "string" && err.message.trim()) return err.message;

  return "An unexpected error occurred. Please try again.";
};

const GroupOfficials = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { data: officialsData, loading: officialsLoading, refetch: refetchOfficials } =
    useQuery(GET_GROUP_OFFICIALS, { client: groupClient });

  const { data: positionsData, loading: positionsLoading, refetch: refetchPositions } =
    useQuery(GET_GROUP_POSITIONS, { client: groupClient });

  const { data: usersData, loading: usersLoading, refetch: refetchUsers } =
    useQuery(GET_USERS, { client: userClient });

  const { data: approvalData, loading: approvalLoading, refetch: refetchApprovals } =
    useQuery(GET_RECORD_APPROVAL_SETTINGS, { client: groupClient });

  const [assignBulk, { loading: savingOfficials }] = useMutation(ASSIGN_POSITIONS_BULK, {
    client: groupClient,
  });

  const [createApprovalSetting, { loading: savingCreate }] = useMutation(
    CREATE_RECORD_APPROVAL_SETTING,
    { client: groupClient }
  );

  const [updateApprovalSetting, { loading: savingUpdate }] = useMutation(
    UPDATE_RECORD_APPROVAL_SETTING,
    { client: groupClient }
  );

  const savingApproval = savingCreate || savingUpdate;

  const groupOfficials = useMemo(() => officialsData?.groupOfficials ?? [], [officialsData]);
  const groupPositions = useMemo(() => positionsData?.groupPositions ?? [], [positionsData]);
  const users = useMemo(() => usersData?.users ?? [], [usersData]);

  const approvalSettings = useMemo(
    () => approvalData?.recordApprovalSettings ?? [],
    [approvalData]
  );

  const currentByPosition = useMemo(() => {
    const map = {};
    for (const o of groupOfficials) {
      if (o?.positionId != null) map[String(o.positionId)] = String(o.userId ?? "");
    }
    return map;
  }, [groupOfficials]);

  const getApproverLabel = (setting) => {
    const a = setting?.approver;

    if (typeof a === "string") return a;

    if (a && typeof a === "object") {
      const name =
        [a.firstName, a.lastName].filter(Boolean).join(" ").trim() ||
        a.username ||
        a.email ||
        a.name;
      if (name) return name;
    }

    const u = users.find((x) => String(x.id) === String(setting?.approverId));
    if (u) {
      return (
        [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
        u.email ||
        `User #${u.id}`
      );
    }

    return "";
  };

  const approvalRows = useMemo(() => {
    const byTable = {};
    for (const s of approvalSettings) byTable[s.tableName] = s;

    return APPROVAL_TABLES.map((t) => {
      const existing = byTable[t.key];
      return {
        id: existing?.id ?? `virtual-${t.key}`,
        tableName: t.key,
        tableLabel: t.label,
        approverId: existing?.approverId ?? null,
        approverName: existing ? getApproverLabel(existing) : "",
        isActive: existing?.isActive ?? true,
        locked: existing?.locked ?? false,
        existsInDb: !!existing?.id,
      };
    });
  }, [approvalSettings, users]);

  // Tabs
  const [tab, setTab] = useState(0);

  // Officials modal
  const [openOfficials, setOpenOfficials] = useState(false);
  const [assignmentsMap, setAssignmentsMap] = useState({});
  const [errorMsg, setErrorMsg] = useState("");

  // Approvals modal
  const [openApprovals, setOpenApprovals] = useState(false);
  const [approvalDraft, setApprovalDraft] = useState(null);
  const [approvalError, setApprovalError] = useState("");

  // Snackbar
  const [successMessage, setSuccessMessage] = useState("");

  const handleOpenOfficials = () => {
    setErrorMsg("");
    setAssignmentsMap({ ...currentByPosition });
    setOpenOfficials(true);
  };

  const handleCloseOfficials = () => {
    setOpenOfficials(false);
    setErrorMsg("");
  };

  const handleSaveOfficials = async () => {
    setErrorMsg("");

    const assignments = Object.entries(assignmentsMap)
      .filter(([posId, userId]) => posId && userId)
      .map(([posId, userId]) => ({
        positionId: Number(posId),
        userId: Number(userId),
      }));

    if (!assignments.length) {
      setErrorMsg("Please assign at least one position.");
      return;
    }

    try {
      const { data } = await assignBulk({ variables: { assignments } });
      const result = data?.assignPositionsBulk;
      if (!result) throw new Error("No response received from server.");

      await Promise.allSettled([refetchOfficials(), refetchPositions(), refetchUsers()]);

      setSuccessMessage(
        `Saved. Inserted: ${result.insertedCount}, Updated: ${result.updatedCount}, Skipped: ${result.skippedCount}`
      );
      setOpenOfficials(false);
    } catch (e) {
      // ✅ show extensions.message if present
      setErrorMsg(getApolloErrorMessage(e));
    }
  };

  const handleOpenApprovals = (row) => {
    setApprovalError("");
    setApprovalDraft({
      ...row,
      approverId: row?.approverId ? String(row.approverId) : "",
    });
    setOpenApprovals(true);
  };

  const handleCloseApprovals = () => {
    setOpenApprovals(false);
    setApprovalDraft(null);
    setApprovalError("");
  };

  const handleSaveApproval = async () => {
    setApprovalError("");

    if (!approvalDraft?.tableName) {
      setApprovalError("Missing tableName.");
      return;
    }
    if (!approvalDraft?.approverId) {
      setApprovalError("Please select an approver.");
      return;
    }

    try {
      if (
        approvalDraft.existsInDb &&
        approvalDraft.id &&
        !String(approvalDraft.id).startsWith("virtual-")
      ) {
        await updateApprovalSetting({
          variables: {
            updatedSetting: {
              id: Number(approvalDraft.id),
              tableName: approvalDraft.tableName,
              approverId: Number(approvalDraft.approverId),
            },
          },
        });
      } else {
        await createApprovalSetting({
          variables: {
            newSetting: {
              tableName: approvalDraft.tableName,
              approverId: Number(approvalDraft.approverId),
            },
          },
        });
      }

      await Promise.allSettled([refetchApprovals(), refetchUsers()]);
      setSuccessMessage("Approval setting saved.");
      handleCloseApprovals();
    } catch (e) {
      // ✅ THIS fixes "Unexpected Execution Error" -> shows extensions.message
      setApprovalError(getApolloErrorMessage(e));
    }
  };

  const officialsColumns = [
    { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
    { field: "username", headerName: "Username", flex: 1.2 },
    { field: "position", headerName: "Position", flex: 1.2 },
    { field: "assignedDate", headerName: "Assigned Date", flex: 1, valueFormatter: ({ value }) => fmtDate(value) },
    { field: "createdDate", headerName: "Created Date", flex: 1, valueFormatter: ({ value }) => fmtDate(value) },
  ];

  const approvalColumns = [
    { field: "tableLabel", headerName: "Process", flex: 1.4 },
    { field: "tableName", headerName: "TableName", flex: 1.2 },
    { field: "approverName", headerName: "Approver", flex: 1.2 },
    {
      field: "locked",
      headerName: "Status",
      flex: 0.8,
      renderCell: ({ value }) => (
        <Chip
          label={value ? "Locked" : "Active"}
          size="small"
          color={value ? "default" : "success"}
          variant={value ? "outlined" : "filled"}
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => handleOpenApprovals(params.row)}
          disabled={!!params.row.locked}
        >
          {params.row.locked ? "Locked" : "Set Approver"}
        </Button>
      ),
    },
  ];

  const loadingOfficials = officialsLoading || positionsLoading || usersLoading;
  const loadingApprovals = approvalLoading || usersLoading;

  return (
    <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
      <Header title="GROUP OFFICIALS" subtitle="Assign positions and manage Maker-Checker approvals" />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant={isMobile ? "scrollable" : "standard"} scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2 }}>
        <Tab label="Officials" />
        <Tab label="Approvals (Maker-Checker)" />
      </Tabs>

      <GroupOfficialsForm
        open={openOfficials}
        onClose={handleCloseOfficials}
        onSubmit={handleSaveOfficials}
        saving={savingOfficials}
        errorMsg={errorMsg}
        positions={groupPositions}
        users={users}
        assignmentsMap={assignmentsMap}
        setAssignmentsMap={setAssignmentsMap}
      />

      <ApprovalSettingsForm
        open={openApprovals}
        onClose={handleCloseApprovals}
        onSubmit={handleSaveApproval}
        saving={savingApproval}
        errorMsg={approvalError}
        users={users}
        draft={approvalDraft}
        setDraft={setApprovalDraft}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4500}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSuccessMessage("")} severity="success" sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {tab === 0 ? (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenOfficials}>
              Assign Officials
            </Button>
          </Box>

          <Box mt="10px" height="75vh" sx={gridSx(theme)}>
            <DataGrid
            rows={groupOfficials.map((row, index) => ({ ...row, sequence: index + 1 }))}
              getRowId={(row) => row.id}
              columns={officialsColumns}
              loading={loadingOfficials}
            />
          </Box>
        </>
      ) : (
        <Box mt="10px" height="75vh" sx={gridSx(theme)}>
          <DataGrid
            rows={approvalRows}
            getRowId={(row) => row.id}
            columns={approvalColumns}
            loading={loadingApprovals}
          />
        </Box>
      )}
    </Box>
  );
};

export default GroupOfficials;
